import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	Grid,
	IconButton,
	Step,
	StepLabel,
	Stepper,
	Toolbar,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { CountryCodeEnum, RolesEnum } from 'features/common/types';
import { getOrderDataByOrderId } from 'features/payment/services';
import { DLocalTitularForm } from 'features/payment/components/DLocalTitularForm';
import { OrderDataToClient } from 'features/payment/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ITransactionFullDetails } from 'features/transaction-details/types';
import { TransactionAuthorizationContext } from 'features/authorization/types/authorizationGuide';
import { buildDLocalOrderFromTransaction } from 'features/transaction-details/helpers/buildDLocalOrderFromTransaction';
import { buildDLocalTitularDefaults } from 'features/transaction-details/helpers/buildDLocalTitularDefaults';
import { fetchPaymentContactDataSafe } from 'features/transaction-details/helpers/fetchPaymentContactData';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import { DLocalTransactionCopyPanel } from '../DLocalTransactionCopyPanel';
import { DLocalPaymentStatusPoller } from '../DLocalPaymentStatusPoller';

const STEPS = ['Crear orden', 'Titular y DLocal'];

interface DLocalCaptureModalProps {
	open: boolean;
	transactionId: number;
	details: ITransactionFullDetails;
	onClose: () => void;
	onPaymentSuccess: () => void;
	onCopied?: (message: string) => void;
}

export const DLocalCaptureModal = ({
	open,
	transactionId,
	details,
	onClose,
	onPaymentSuccess,
	onCopied,
}: DLocalCaptureModalProps): JSX.Element => {
	const { auth } = useSelector((state: RootState) => state);
	const [activeStep, setActiveStep] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [orderData, setOrderData] = useState<OrderDataToClient | null>(null);
	const [pollingActive, setPollingActive] = useState(false);
	const [pollFailedMessage, setPollFailedMessage] = useState<string | null>(null);
	const [pollingExhausted, setPollingExhausted] = useState(false);
	const [lastPollStatus, setLastPollStatus] = useState<string | null>(null);
	const [pollSession, setPollSession] = useState(0);
	const [dlocalCheckoutOpened, setDlocalCheckoutOpened] = useState(false);
	const [paymentConfirmed, setPaymentConfirmed] = useState(false);
	const [contactForForm, setContactForForm] = useState<{ name: string; email: string; phone: string } | null>(null);

	const td = details.transaction.transactionData;

	const isAdminOrOperator =
		auth.user?.role === RolesEnum.SUPERADMIN ||
		auth.user?.role === RolesEnum.ADMIN ||
		auth.user?.role === RolesEnum.OPERATOR;

	const transactionContext: TransactionAuthorizationContext = useMemo(
		() => ({
			transactionId,
			coinCode: td.coinCode,
			pendingAuthorization: td.pendingAuthorization,
			fee: td.fee,
			cardStamp: td.cardStamp,
			cardholder: td.cardholder,
			document: td.document,
			cardNumberFormatted: td.cardNumberFormatted,
			cardExpiration: td.cardExpiration,
			cardSecurityCodeFormatted: td.cardSecurityCodeFormatted,
			flagShowCardInfo: details.transaction.flagShowCardInfo,
		}),
		[transactionId, td, details.transaction.flagShowCardInfo],
	);

	const resetState = useCallback((): void => {
		setActiveStep(0);
		setLoading(false);
		setError(null);
		setOrderData(null);
		setPollingActive(false);
		setPollFailedMessage(null);
		setPollingExhausted(false);
		setLastPollStatus(null);
		setPollSession(0);
		setDlocalCheckoutOpened(false);
		setPaymentConfirmed(false);
		setContactForForm(null);
	}, []);

	const createOrder = useCallback(async (): Promise<void> => {
		setLoading(true);
		setError(null);
		try {
			const orderResult = await buildDLocalOrderFromTransaction({
				details,
				plCountryCode: auth.country,
			});
			const orderResponse = await getOrderDataByOrderId(orderResult.id);
			const data = orderResponse.data;

			if (data.wasTransacted) {
				throw new Error('Esta orden ya fue utilizada. Cerrá el modal e intentá de nuevo.');
			}
			if (data.expired) {
				throw new Error('La orden está expirada. Cerrá el modal e intentá de nuevo.');
			}

			setOrderData(data);
			setActiveStep(1);
		} catch (err: unknown) {
			setError(extractErrorMessage(err, 'No se pudo crear la orden de pago DLocal'));
		} finally {
			setLoading(false);
		}
	}, [auth.country, details]);

	useEffect(() => {
		if (open) {
			resetState();
		}
	}, [open, resetState]);

	useEffect(() => {
		if (open && activeStep === 0 && !loading && !orderData && !error) {
			void createOrder();
		}
	}, [open, activeStep, loading, orderData, error, createOrder]);

	useEffect(() => {
		if (!orderData || activeStep !== 1) return;
		let cancelled = false;

		const loadContact = async (): Promise<void> => {
			const contact = await fetchPaymentContactDataSafe(td.crmTicket, td.isBitrixDeal);
			if (!cancelled) {
				setContactForForm(contact);
			}
		};

		void loadContact();
		return () => {
			cancelled = true;
		};
	}, [orderData?.orderId, activeStep, td.crmTicket, td.isBitrixDeal]);

	const titularDefaults = useMemo(() => {
		if (!orderData) return null;
		return buildDLocalTitularDefaults(orderData, contactForForm, td.cardholder, td.document);
	}, [orderData, contactForForm, td.cardholder, td.document]);

	const handleRedirectStarted = (): void => {
		setDlocalCheckoutOpened(true);
		setPollingActive(true);
		setPollFailedMessage(null);
		setPollingExhausted(false);
		setLastPollStatus(null);
	};

	const handlePaid = (): void => {
		setPollingActive(false);
		setPollingExhausted(false);
		setPaymentConfirmed(true);
		window.setTimeout(() => {
			onPaymentSuccess();
			onClose();
		}, 1800);
	};

	const handlePollFailed = (message: string): void => {
		setPollingActive(false);
		setPollFailedMessage(message);
	};

	const handlePollingExhausted = (message: string, lastStatus: string): void => {
		setPollingActive(false);
		setPollingExhausted(true);
		setLastPollStatus(lastStatus);
		setPollFailedMessage(null);
	};

	const handleAssumePaymentAndClose = (): void => {
		onPaymentSuccess();
		onClose();
	};

	const handleRetryPolling = (): void => {
		setPollFailedMessage(null);
		setPollingExhausted(false);
		setLastPollStatus(null);
		setPollSession((s) => s + 1);
		setPollingActive(true);
	};

	const pendingLabel = formatTransactionAmount(td.pendingAuthorization, td.coinCode);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<Toolbar sx={{ justifyContent: 'space-between' }}>
				<Typography variant="h6" fontWeight="bold">
					Cobrar con DLocal
				</Typography>
				<IconButton onClick={onClose} aria-label="Cerrar">
					<CloseIcon />
				</IconButton>
			</Toolbar>

			<DialogContent dividers>
				<Stepper activeStep={activeStep} sx={{ mb: 3 }}>
					{STEPS.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>

				<Alert severity="info" sx={{ mb: 2 }}>
					Cobro interno por <strong>{pendingLabel}</strong> (pendiente de autorizar). Caso/deal:{' '}
					<strong>{td.crmTicket}</strong>
				</Alert>

				{activeStep === 0 && (
					<Box sx={{ textAlign: 'center', py: 4 }}>
						{error ? (
							<>
								<Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
									{error}
								</Alert>
								<Button variant="contained" onClick={() => void createOrder()}>
									Reintentar
								</Button>
							</>
						) : (
							<>
								<CircularProgress sx={{ mb: 2 }} />
								<Typography color="text.secondary">Creando orden de pago DLocal…</Typography>
							</>
						)}
					</Box>
				)}

				{activeStep === 1 && orderData && titularDefaults && (
					<Box>
						{paymentConfirmed && (
							<Alert severity="success" sx={{ mb: 2 }}>
								<Typography variant="body2" fontWeight="bold">
									Pago confirmado en DLocal
								</Typography>
								<Typography variant="body2">
									El cobro fue registrado. Cerrando para que puedas finalizar la transacción…
								</Typography>
							</Alert>
						)}

						{dlocalCheckoutOpened && !pollingExhausted && !paymentConfirmed && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								<Typography variant="body2" fontWeight="bold" gutterBottom>
									Checkout DLocal abierto en otra pestaña
								</Typography>
								<Typography variant="body2">
									Copiá número de tarjeta, vencimiento y CVC desde el panel izquierdo. Esta ventana consulta el pago
									cada 10 segundos (hasta 20 veces).
								</Typography>
							</Alert>
						)}

						{pollingExhausted && (
							<Alert severity="info" sx={{ mb: 2 }}>
								<Typography variant="body2" fontWeight="bold" gutterBottom>
									Ya no consultamos el estado del pago automáticamente
								</Typography>
								<Typography variant="body2" gutterBottom>
									Se realizaron 20 consultas sin recibir confirmación
									{lastPollStatus ? ` (último estado: ${lastPollStatus})` : ''}. Revisá en la orden / cobros al cliente
									si el pago DLocal ya figura ingresado.
								</Typography>
								<Typography variant="body2">
									Si el cobro es correcto, podés darlo por cobrado y <strong>finalizar la transacción</strong> en la
									pantalla principal para dar de baja el pendiente de autorización.
								</Typography>
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
									<Button variant="contained" size="small" onClick={handleAssumePaymentAndClose}>
										Cerrar y finalizar transacción
									</Button>
									<Button variant="outlined" size="small" onClick={handleRetryPolling}>
										Reintentar consultas
									</Button>
								</Box>
							</Alert>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12} md={5}>
								<DLocalTransactionCopyPanel
									transactionContext={transactionContext}
									flagShowCardInfo={details.transaction.flagShowCardInfo}
									isAdminOrOperator={isAdminOrOperator}
									onCopied={onCopied}
								/>
							</Grid>
							<Grid item xs={12} md={7}>
								<DLocalTitularForm
									orderData={orderData}
									clientLocation={null}
									countryCode={auth.country || CountryCodeEnum.URUGUAY}
									defaultValues={titularDefaults}
									redirectTarget="new_tab"
									submitLabel="Abrir checkout DLocal"
									showOrderSummary={false}
									submitDisabled={dlocalCheckoutOpened}
									onRedirectStarted={handleRedirectStarted}
								/>
							</Grid>
						</Grid>

						{(pollingActive || pollingExhausted) && orderData && (
							<Box sx={{ mt: 2 }}>
								<DLocalPaymentStatusPoller
									key={`${orderData.orderId}-${pollSession}`}
									orderId={orderData.orderId}
									active={pollingActive}
									compact
									onPaid={handlePaid}
									onFailed={handlePollFailed}
									onPollingExhausted={handlePollingExhausted}
								/>
							</Box>
						)}

						{pollFailedMessage && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{pollFailedMessage}
								<Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={handleRetryPolling}>
									Reintentar consulta de pago
								</Button>
							</Alert>
						)}
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};
